import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { CreditAdjustmentModal } from '@/components/admin/CreditAdjustmentModal';
import { UserDetailsModal } from '@/components/admin/UserDetailsModal';
import { PurgeAccountModal } from '@/components/admin/PurgeAccountModal';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAdminUsers, AdminUser } from '@/hooks/admin/useAdminUsers';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatRelativeTime } from '@/utils/date';
import {
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    CircleStackIcon,
    EyeIcon,
    FireIcon,
    ExclamationTriangleIcon,
    KeyIcon,
} from '@heroicons/react/24/outline';

export function AdminUsersPage() {
    usePageTitle('Admin - Users');

    const [page, setPage] = useState(1);
    const [emailSearch, setEmailSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
    const [detailsUser, setDetailsUser] = useState<AdminUser | null>(null);
    const [purgeUser, setPurgeUser] = useState<AdminUser | null>(null);
    const limit = 20;

    const debouncedSearch = useDebouncedValue(emailSearch, 300);

    const { data, isLoading } = useAdminUsers(
        page,
        limit,
        debouncedSearch || undefined
    );

    const handleSearchChange = (value: string) => {
        setEmailSearch(value);
        setPage(1);
    };

    if (isLoading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center gap-4">
                <Link
                    to="/admin"
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Admin
                </Link>
            </div>

            <PageHeader title="Users" />

            {/* Search */}
            <div className="flex items-center gap-4">
                <div className="relative w-80">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <Input
                        type="text"
                        placeholder="Search by email..."
                        value={emailSearch}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            {/* Users Table */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-secondary">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                            <tr>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    ID
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Email
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Status
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Credit Balance
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Created
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Last Login
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.users.map((user) => (
                                <tr
                                    key={user.id}
                                    className="border-b border-gray-200 dark:border-gray-700"
                                >
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                        {user.id}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {user.email}
                                            </p>
                                            {user.roles.includes('ROLE_ADMIN') && (
                                                <span className="text-xs text-primary-600 dark:text-primary-400">
                                                    Admin
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {user.verified ? (
                                                <span className="rounded-sm bg-green-100 px-2 py-1 text-xs font-medium text-green-700 dark:bg-green-900/20 dark:text-green-400">
                                                    Verified
                                                </span>
                                            ) : (
                                                <span className="rounded-sm bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                                                    Unverified
                                                </span>
                                            )}
                                            {user.deletionRequestedAt && (
                                                <span className="rounded-sm bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400">
                                                    Deletion Requested
                                                </span>
                                            )}
                                            {user.failedLoginAttempts > 5 && (
                                                <ExclamationTriangleIcon
                                                    className="h-5 w-5 text-amber-500"
                                                    title={`${user.failedLoginAttempts} failed login attempts`}
                                                />
                                            )}
                                            {user.passkeysCount > 0 && (
                                                <KeyIcon
                                                    className="h-5 w-5 text-blue-500"
                                                    title={`${user.passkeysCount} passkey${user.passkeysCount > 1 ? 's' : ''} enabled`}
                                                />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                            {user.creditBalance}
                                        </span>
                                        <span className="text-gray-500"> credits</span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                        {new Date(user.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                        {formatRelativeTime(user.lastLoginAt)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() =>
                                                    setDetailsUser(user)
                                                }
                                                title="View Details"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() =>
                                                    setSelectedUser(user)
                                                }
                                                title="Adjust Credits"
                                            >
                                                <CircleStackIcon className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() =>
                                                    setPurgeUser(user)
                                                }
                                                title="Purge Account"
                                                className="text-red-600 hover:text-red-700 dark:text-red-400"
                                            >
                                                <FireIcon className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {data && data.total > 0 && (
                    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Showing {(page - 1) * limit + 1} to{' '}
                            {Math.min(page * limit, data.total)} of {data.total} users
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                            >
                                <ChevronLeftIcon className="h-4 w-4" />
                                Previous
                            </Button>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Page {page} of {data.totalPages}
                            </span>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setPage(page + 1)}
                                disabled={page >= data.totalPages}
                            >
                                Next
                                <ChevronRightIcon className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {data?.users.length === 0 && (
                    <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                        No users found
                    </div>
                )}
            </div>

            {/* Credit Adjustment Modal */}
            {selectedUser && (
                <CreditAdjustmentModal
                    user={selectedUser}
                    onClose={() => setSelectedUser(null)}
                />
            )}

            {/* User Details Modal */}
            {detailsUser && (
                <UserDetailsModal
                    user={detailsUser}
                    onClose={() => setDetailsUser(null)}
                />
            )}

            {/* Purge Account Modal */}
            {purgeUser && (
                <PurgeAccountModal
                    user={purgeUser}
                    onClose={() => setPurgeUser(null)}
                />
            )}
        </>
    );
}
